import * as React from "react"
import { Link } from "react-router-dom"
import {
  type Category,
  CategoryState,
  type EntityCategory,
} from "@digits-graphql/frontend/graphql-bearer"
import type { DigitsRoute } from "@digits-shared/components/Router/DigitsRoute"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronRight } from "@digits-shared/components/SVGIcons/line/ChevronRight.svg"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import { CATEGORY_COLOR_MAP } from "src/frontend/components/OS/Applications/CategoryManager/Shared"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import { useStopClickPropagation } from "src/shared/hooks/useStopClickPropagation"

const Chevron = styled(SvgChevronRight)`
  ${svgPathStyles(colors.secondary50, 2)};
  flex-shrink: 0;
`

const LinkCrumb = styled(Link)<{ color?: string }>`
  color: ${({ color }) => color || colors.secondary80};

  &:hover {
    text-decoration: underline;
  }
`

const StaticCrumb = styled.div<{ color?: string }>`
  color: ${({ color }) => color || colors.secondary80};
`

const Crumbs = styled.div<{ $small?: boolean; $hidden?: boolean; width?: number }>`
  width: ${({ width }) => width || 220}px;
  display: flex;
  gap: 2px;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${StaticCrumb}, ${LinkCrumb} {
    ${({ $small }) => ($small ? fonts.scale.label : fonts.scale.detail)};

    &:first-child {
      font-weight: ${fonts.weight.heavy};
    }
  }

  ${Chevron} {
    width: ${({ $small }) => ($small ? 10 : 14)}px;
    height: ${({ $small }) => ($small ? 10 : 14)}px;
  }

  ${({ $hidden }) =>
    $hidden
      ? css`
          visibility: hidden;
          position: absolute;
          top: 0;
          left: 0;
          pointer-events: none;
          white-space: nowrap;
          overflow: hidden;
        `
      : css`
          ${LinkCrumb}, ${StaticCrumb} {
            &:last-child {
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        `}
`

type SyntheticBreadcrumb = { name: string; id: string; noLink: boolean }
type Breadcrumb = EntityCategory | SyntheticBreadcrumb

interface CategoryBreadcrumbsProps {
  className?: string
  category: Category
  small?: boolean
  route?: DigitsRoute
  width?: number
  allowClickPropagation?: boolean
  openInNewTab?: boolean
}

interface CrumbProps {
  breadcrumb: Breadcrumb
  color?: string
  route?: DigitsRoute
  allowClickPropagation?: boolean
  openInNewTab?: boolean
}

export const CategoryBreadcrumbs: React.FC<CategoryBreadcrumbsProps> = ({
  className,
  category,
  small,
  route,
  width,
  allowClickPropagation = false,
  openInNewTab = false,
}) => {
  const { isTextOverflowing, textElementRef } = useTextIsOverflowing<HTMLDivElement>()

  const { nodes, abbreviatedNodes, crumbs } = useBreadcrumbs({
    category,
    small,
    route,
    allowClickPropagation,
    openInNewTab,
  })
  const tooltipContent = useBreadcrumbsString(crumbs)

  return (
    <>
      {/* Mounting a hidden version of the breadcrumbs to measure text overflow */}
      <Crumbs
        className={className}
        $small={small}
        ref={textElementRef}
        $hidden={true}
        width={width}
      >
        {nodes}
      </Crumbs>

      <DigitsTooltip content={tooltipContent} disabled={!isTextOverflowing} maxWidth={200}>
        <Crumbs className={className} $small={small} width={width}>
          {isTextOverflowing ? abbreviatedNodes : nodes}
        </Crumbs>
      </DigitsTooltip>
    </>
  )
}

const Crumb: React.FC<CrumbProps> = ({
  breadcrumb,
  color,
  route,
  allowClickPropagation,
  openInNewTab,
}) => {
  const generatePath = useFrontendPathGenerator()
  const stopPropagation = useStopClickPropagation()

  if ((breadcrumb as SyntheticBreadcrumb).noLink || !route) {
    return <StaticCrumb color={color}>{breadcrumb.name}</StaticCrumb>
  }

  return (
    <LinkCrumb
      to={generatePath(route, { categoryId: breadcrumb.id })}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
      color={color}
      onClick={allowClickPropagation ? undefined : stopPropagation}
    >
      {breadcrumb.name}
    </LinkCrumb>
  )
}

const useBreadcrumbs = ({
  category,
  small,
  route,
  allowClickPropagation,
  openInNewTab,
}: CategoryBreadcrumbsProps) => {
  const { type, ancestors, parentCategoryId, categoryState } = category

  const breadcrumbs = React.useMemo(() => {
    if (!parentCategoryId) return [{ name: "Digits", id: "root-breadcrumb", noLink: true }]
    return ancestors && ancestors.length > 0
      ? [...ancestors].reverse()
      : [
          {
            name: stringHelper.camelCaseToSpaces(type, true),
            id: "type-breadcrumb",
            noLink: true,
          },
        ]
  }, [ancestors, parentCategoryId, type])

  const breadcrumbNodes = breadcrumbs.map((breadcrumb, index) => (
    <React.Fragment key={breadcrumb.id}>
      {index !== 0 && <Chevron />}
      <Crumb
        breadcrumb={breadcrumb}
        route={route}
        openInNewTab={openInNewTab}
        allowClickPropagation={allowClickPropagation}
        color={
          index === 0 && categoryState === CategoryState.CategoryActive
            ? CATEGORY_COLOR_MAP.get(type)
            : categoryState === CategoryState.CategoryDisabled
              ? colors.secondary50
              : colors.secondary80
        }
      />
    </React.Fragment>
  ))

  const lastBreadcrumb = breadcrumbNodes[breadcrumbNodes.length - 1]
  const firstBreadcrumb = breadcrumbNodes[0]

  return React.useMemo(() => {
    const abbreviatedCrumbs =
      breadcrumbs.length > 2 &&
      lastBreadcrumb !== firstBreadcrumb &&
      lastBreadcrumb &&
      firstBreadcrumb
        ? // create a new array of the first and last breadcrumb node (if they're different and there are more than two nodes)
          [
            firstBreadcrumb,
            <Chevron key="abbreviated-chevron" />,
            <StaticCrumb key="abbreviated-static-crumb">...</StaticCrumb>,
            lastBreadcrumb,
          ]
        : breadcrumbNodes

    return {
      nodes: breadcrumbNodes,
      abbreviatedNodes: abbreviatedCrumbs,
      crumbs: breadcrumbs,
    }
  }, [breadcrumbs, lastBreadcrumb, firstBreadcrumb, breadcrumbNodes])
}

const useBreadcrumbsString = (breadcrumbs: Breadcrumb[]) =>
  breadcrumbs.map((breadcrumb) => breadcrumb.name).join(" > ")
